// ============================================================================
// useComponentCapabilities.ts
// Memoized component capability checks
// ============================================================================

import { useMemo } from "react";
import type { TComponentCapabilities, TComponentGroups } from "@/types/filter";

const COMPONENT_GROUPS: TComponentGroups = {
  withToggle: ["agent-performance"],
  withSearchDropdown: [
    "employee",
    "evaluation-list",
    "employee-management",
    "manage-user",
    "services",
    "campaign",
    "role-management",
  ],
  withEmployeeAttrDropdown: [
    "evaluation-list",
    "manage-user",
    "employee-management",
  ],
  withStatus: [
    "employee",
    "push-api-clients",
    "push-api-clients-history",
    "employee-management",
    "employee-attributes",
    "role-management",
  ],
  withIngestionType: ["data-ingestion"],
  withLocation: ["manage-user"],
  withDateType: [
    "evaluation-list",
    "evaluation-dashboard",
    "agent-performance-dashboard",
    "agent-performance-list",
    "violations-by-category",
    "failed-category-calls",
    "supervisor-teams-report",
    "campaigns-wise-team-report",
    "supervisor-team-detail",
    "campaign-wise-team-detail",
    "analytics-dashboard-global",
  ],
  withoutCallDateMenuItems: [],
  withStartEndDate: [
    "agent-performance",
    "agent-performance-dashboard",
    "push-api-clients",
    "push-api-clients-history",
    "evaluation-dashboard",
    "evaluation-list",
    "agent-performance-list",
    "violations-by-category",
    "failed-category-calls",
    "supervisor-teams-report",
    "campaigns-wise-team-report",
    "supervisor-team-detail",
    "campaign-wise-team-detail",
    "customer-insights",
    "analytics-dashboard-global",
    "ai-engine-configurations",
  ],
  withCampaign: [
    "pii-configuration-filter",
    "pii-pattern-filter",
    "pii-denyterm-filter",
    "pii-entity-filter",
    "agent-performance",
    "agent-performance-list",
    "violations-by-category",
    "services",
    "overall-sentiment-analysis",
    "evaluation-dashboard",
    "evaluation-list",
    "agent-performance-dashboard",
    "failed-category-calls",
    "push-api-clients-history",
    "selection-criteria",
    "customer-insights",
    "analytics-dashboard-global",
    "stt-engine-configurations",
  ],
  withEntity: [
    "campaign",
    "data-ingestion",
    "services",
    "analytics-query-builder",
  ],
  withWeeklyPeriod: [
    "overall-sentiment-analysis",
    "agent-performance-dashboard",
    "evaluation-dashboard",
    "evaluation-list",
    "supervisor-teams-report",
    "campaigns-wise-team-report",
    "supervisor-team-detail",
    "campaign-wise-team-detail",
    "personalized-coaching-insights",
    "analytics-dashboard-global",
    "agent-performance-list",
  ],
  withAgent: [
    "agent-performance-list",
    "violations-by-category",
    "personalized-coaching-insights",
    "overall-sentiment-analysis",
    "evaluation-list",
    "push-api-clients-history",
    "customer-insights",
    "analytics-dashboard-global",
  ],
  withScore: [],
  withSupervisor: [
    "violations-by-category",
    "overall-sentiment-analysis",
    "customer-insights",
    "evaluation-list",
    "agent-performance-list",
  ],
  withService: [
    "overall-sentiment-analysis",
    "evaluation-list",
    "campaign",
    "analytics-query-builder",
    "agent-performance-list",
  ],
  withYearMonth: [""],
  withTemplate: [
    "analytics-query-builder",
    "selection-criteria",
    "agent-performance-list",
  ],
  withAiEngine: ["ai-engine-configurations"],
  withFrequency: ["ai-engine-configurations"],
  withAiCompliance: ["ai-engine-configurations"],
  withPermissionCountRange: ["role-management"],
  withoutSelectDateOptionInPeriod: ["overall-sentiment-analysis"],
  withoutClearFilterChipButton: [
    "employee-management",
    "employee-attributes",
    "agent-performance-list",
    "role-management",
  ],
  withThisWeekOption: [
    "agent-performance-dashboard",
    "evaluation-dashboard",
    "evaluation-list",
    "supervisor-teams-report",
    "campaigns-wise-team-report",
    "supervisor-team-detail",
    "campaign-wise-team-detail",
    "personalized-coaching-insights",
    "agent-performance-list",
  ],
  withSelectCustomizeDate: [
    "agent-performance-dashboard",
    "evaluation-dashboard",
    "evaluation-list",
    "agent-performance-list",
    "supervisor-teams-report",
    "campaigns-wise-team-report",
    "supervisor-team-detail",
    "campaign-wise-team-detail",
    "analytics-dashboard-global",
  ],
  withHierarchyViewType: [""],
  withRankBasedAuditor: [],
  withCSATInput: ["agent-performance-list"],
  withFCRInput: ["agent-performance-list"],
  withProfanityInput: ["agent-performance-list"],
  withAIScoreRange: ["agent-performance-list"],
  withQAScoreRange: ["agent-performance-list"],
  withAchievement: ["agent-performance-list"],
  withTargetLanguage: ["stt-engine-configurations"],
} as const;

export const useComponentCapabilities = (
  component: string,
): TComponentCapabilities => {
  return useMemo(() => {
    if (!component) {
      return {
        hasToggle: false,
        hasSearchDropdown: false,
        hasStatus: false,
        hasIngestionType: false,
        hasLocation: false,
        hasDateType: false,
        withoutCallDateMenuItems: false,
        hasStartEndDate: false,
        hasCampaign: false,
        hasEntity: false,
        hasWeeklyPeriod: false,
        hasAgent: false,
        hasScore: false,
        hasSupervisor: false,
        hasService: false,
        hasYearMonth: false,
        hasTemplate: false,
        hasAiEngine: false,
        hasFrequency: false,
        hasAiCompliance: false,
        hasRankBasedAuditor: false,
        hasPermissionCountRange: false,
        withoutSelectDateOptionInPeriod: false,
        hasThisWeekOption: false,
        hasCustomizeDate: false,
        hasHierarchyScopeType: false,
        withoutClearFilterChipButton: false,
        hasCSAT: false,
        hasFCR: false,
        hasProfanity: false,
        hasAIScoreRange: false,
        hasQAScoreRange: false,
        hasAchievement: false,
        hasEmployeeAttrDropdown: false,
        hasTargetLanguage: false,
      };
    }

    return {
      hasToggle: COMPONENT_GROUPS.withToggle.includes(component),
      hasSearchDropdown:
        COMPONENT_GROUPS.withSearchDropdown.includes(component),
      hasStatus: COMPONENT_GROUPS.withStatus.includes(component),
      hasIngestionType: COMPONENT_GROUPS.withIngestionType.includes(component),
      hasLocation: COMPONENT_GROUPS.withLocation.includes(component),
      hasDateType: COMPONENT_GROUPS.withDateType.includes(component),
      withoutCallDateMenuItems:
        COMPONENT_GROUPS.withoutCallDateMenuItems.includes(component),
      hasStartEndDate: COMPONENT_GROUPS.withStartEndDate.includes(component),
      hasCampaign: COMPONENT_GROUPS.withCampaign.includes(component),
      hasEntity: COMPONENT_GROUPS.withEntity.includes(component),
      hasWeeklyPeriod: COMPONENT_GROUPS.withWeeklyPeriod.includes(component),
      hasAgent: COMPONENT_GROUPS.withAgent.includes(component),
      hasScore: COMPONENT_GROUPS.withScore.includes(component),
      hasSupervisor: COMPONENT_GROUPS.withSupervisor.includes(component),
      hasService: COMPONENT_GROUPS.withService.includes(component),
      hasYearMonth: COMPONENT_GROUPS.withYearMonth.includes(component),
      hasTemplate: COMPONENT_GROUPS.withTemplate.includes(component),
      hasAiEngine: COMPONENT_GROUPS.withAiEngine.includes(component),
      hasFrequency: COMPONENT_GROUPS.withFrequency.includes(component),
      hasAiCompliance: COMPONENT_GROUPS.withAiCompliance.includes(component),
      hasRankBasedAuditor:
        COMPONENT_GROUPS.withRankBasedAuditor.includes(component),
      hasPermissionCountRange:
        COMPONENT_GROUPS.withPermissionCountRange.includes(component),
      withoutSelectDateOptionInPeriod:
        COMPONENT_GROUPS.withoutSelectDateOptionInPeriod.includes(component),
      hasThisWeekOption:
        COMPONENT_GROUPS.withThisWeekOption.includes(component),
      hasCustomizeDate:
        COMPONENT_GROUPS.withSelectCustomizeDate.includes(component),
      hasHierarchyScopeType:
        COMPONENT_GROUPS.withHierarchyViewType.includes(component),
      withoutClearFilterChipButton:
        COMPONENT_GROUPS.withoutClearFilterChipButton.includes(component),
      hasCSAT: COMPONENT_GROUPS.withCSATInput.includes(component),
      hasFCR: COMPONENT_GROUPS.withFCRInput.includes(component),
      hasProfanity: COMPONENT_GROUPS.withProfanityInput.includes(component),
      hasAIScoreRange: COMPONENT_GROUPS.withAIScoreRange.includes(component),
      hasQAScoreRange: COMPONENT_GROUPS.withQAScoreRange.includes(component),
      hasAchievement: COMPONENT_GROUPS.withAchievement.includes(component),
      hasEmployeeAttrDropdown:
        COMPONENT_GROUPS.withEmployeeAttrDropdown.includes(component),
      hasTargetLanguage:
        COMPONENT_GROUPS.withTargetLanguage.includes(component),
    };
  }, [component]);
};
